import { CampaignResultMessage, CampaignResultsData, CampaignResultStatus } from "../models/CampaignResults";
import { DateTime } from "luxon";
import {
  Bot,
  Chat,
  ChatTab,
  CampaignInteractionStatus,
  Message,
  MetricData,
  RequestStatus,
  Schedule,
  User,
  UserAssignedChat,
  LiveStream,
  LiveChatCard,
  LiveChatFilters,
  Campaign,
  ChatAssignmentType,
  PerCardChatAssignmentConfig,
  ChatChannel,
  CampaignDataType,
  Booking,
} from "../models";
import { EmbeddingData } from "../models/HelpCenter";
import { logError } from "./logging";
import auth from "../stores/auth";

const botAdapter = ({
  id,
  image,
  language,
  name,
  name_page,
  page_id,
  webhook_key,
  webhook_password,
  agents_schedule,
  livecard_schedule,
  schedule_version,
  schedule,
  agents_schedule_message,
  agents_schedule_dialog,
  fb_country,
  feature_flags,
  has_video_call_enabled,
  closing_dialog,
  cc_journey_id,
  closing_dialog_type,
  closing_dialog_schedule,
  inactivity_dialog,
  inactivity_dialog_schedule,
  max_chat_assigned,
  chat_assignment_type,
  auto_assign_chat_user_types,
  edna_bot_enabled,
  queue_position_enabled,
  queue_position_dialog,
  auto_assign_request_on_next_day_enabled,
  chat_transferred_timeout,
  auto_assign_deal_user_types,
  workspace_id,
  auto_assign_chat_round_robin_chat,
  round_robin_chat_timeout,
  per_card_chat_assignment_config,
  disable_acm_for_chat_transfer,
  disable_ccd_for_chat_transfer,
  bot_mode,
  whatsapp_enabled,
  whatsapp_provider,
  voice_call_provider,
  voice_call_enabled,
  qf_journey_id,
  qf_journey_config,
  conversation_status_config,
  whatsapp_call_enabled,
  whatsapp_call_config,
}: any): Bot => ({
  id,
  workspaceId: workspace_id,
  image,
  language,
  name,
  facebook: {
    pageName: name_page,
    pageId: page_id,
  },
  key: webhook_key,
  password: webhook_password,
  settings: {
    botMode: bot_mode,
    timezone: fb_country,
    agentsOutOfScheduleMessage: agents_schedule_message || "",
    agentsOutOfScheduleDialog: agents_schedule_dialog || null,
    scheduledChatClosingDialog: closing_dialog,
    scheduledChatClosingJourneyId: cc_journey_id,
    scheduledChatClosingDialogType: closing_dialog_type,
    scheduledChatClosingDialogAt: closing_dialog_schedule,
    inactivityDialog: inactivity_dialog,
    inactivityDialogAt: inactivity_dialog_schedule,
    ...(agents_schedule
      ? { agentsSchedule: agents_schedule && JSON.parse(agents_schedule) }
      : {}),
    scheduleVersion: schedule_version,
    schedule: schedule,
    livecardSchedule: livecard_schedule,
    maxChatAssigned: max_chat_assigned,
    chatAssignmentType: chat_assignment_type,
    autoAssignChatUserTypes: auto_assign_chat_user_types,
    ednaBotEnabled: edna_bot_enabled,
    queuePositionEnabled: queue_position_enabled,
    queuePositionDialog: queue_position_dialog,
    enableAutoAssignRequestOnNextDay: auto_assign_request_on_next_day_enabled,
    chatTransferredTimeout: chat_transferred_timeout,
    autoAssignDealUserTypes: auto_assign_deal_user_types,
    autoAssignRoundRobinChat: auto_assign_chat_round_robin_chat,
    roundRobinChatTimeout: round_robin_chat_timeout,
    perCardChatAssignmentConfig: per_card_chat_assignment_config ?? [],
    disableACMForChatTransfer: disable_acm_for_chat_transfer,
    disableCCDForChatTransfer: disable_ccd_for_chat_transfer,
    whatsappEnabled: whatsapp_enabled,
    whatsappProvider: whatsapp_provider,
    voiceCallEnabled: voice_call_enabled,
    voiceCallProvider: voice_call_provider,
    qfJourneyId: qf_journey_id,
    qfJourneyConfig: qf_journey_config,
    conversationStatusConfig: conversation_status_config,
    whatsappCallEnabled: whatsapp_call_enabled,
    whatsappCallConfig: whatsapp_call_config,
  },
  featureFlags: feature_flags || [],
  hasVideoCallEnabled: has_video_call_enabled,
});

export const chatAdapter = ({
  id,
  profile_pic,
  first_name,
  last_name,
  from_channel,
  updated_at,
  new_messages,
  livechat,
  flag,
  current_agent,
  data,
  labels,
  last_msg_date,
  user_last_msg_date,
  request_status,
  previous_agent,
  disable_video_call,
  is_starred,
  display_as_contact,
  contact_created_at,
  primary_contact_for,
  blocked,
  priority,
  archived,
  has_interaction_with_agent,
  last_live_card_id,
  delayed_disconnect,
  sender_id,
  conversation_status_id,
  forward_bot,
}: any): Chat => {
  const senderId = data
    ? JSON.parse(data)?.user_data?.user_data?.fb_id
    : sender_id;

  const model: Chat = {
    id,
    senderId,
    username: data
      ? JSON.parse(data)?.user_data?.user_data?.username
      : undefined,
    image: profile_pic,
    firstName: first_name,
    lastName: last_name,
    channel: from_channel,
    newMessagesCount: new_messages,
    hasRequestedAgent: flag === 0,
    isConnected: livechat === 0,
    userConnected: current_agent,
    starred: is_starred,
    lastMessage: last_msg_date
      ? DateTime.fromISO(last_msg_date).toJSDate()
      : undefined,
    userLastMessage: user_last_msg_date
      ? DateTime.fromISO(user_last_msg_date).toJSDate()
      : undefined,
    contactCreatedAt: contact_created_at
      ? DateTime.fromFormat(contact_created_at, "yyyy-MM-dd HH:mm:ss", {
          zone: "utc",
        })
          .toLocal()
          .toJSDate()
      : undefined,
    updatedAt: DateTime.fromISO(last_msg_date || updated_at).toJSDate(),
    data: data ? JSON.parse(data)?.user_data?.user_data : undefined,
    labels: labels ? JSON.parse(labels) : undefined,
    requestStatus: request_status,
    previousUserConnected: previous_agent,
    disableVideoCall: disable_video_call,
    isContact: display_as_contact,
    merged: primary_contact_for ? JSON.parse(primary_contact_for) : null,
    isBlocked: blocked,
    priority,
    archived,
    hasInteractionWithAgent: has_interaction_with_agent,
    lastLiveCardId: last_live_card_id,
    delayedDisconnect: delayed_disconnect,
    conversationStatusId: conversation_status_id,
    forwardBot: forward_bot,
  };

  if (model.data) {
    if (model.data.test) delete model.data.test;

    Object.entries(model.data).forEach(([k, v]) => {
      if (typeof v !== "string" && model.data) {
        model.data[k] = JSON.stringify(v);
      }
    });
  }

  return model;
};

export const messageAdapter = ({
  id,
  text,
  delivery_status,
  error_code,
  error_message,
  sender_id,
  sender_type,
  created_at,
  first_name,
  last_name,
  meta_type,
  channel_message_id,
}: any): Message => {
  const isFromUser = sender_type === 0;
  // eslint-disable-next-line no-control-regex
  const data = JSON.parse(text.replace(/[\x00-\x1F\x7F-\x9F]/g, ""));

  let textMessage = "";
  let repliedOf;
  let reaction;
  let isDeleted = false;
  let attachments;
  let attachment;
  const confidential = data?.confidential;

  if (isFromUser) {
    if (data["entry"][0]["messaging"][0]["message"]) {
      textMessage = data["entry"][0]["messaging"][0]["message"]["text"];
      if (!data?.meta) {
        data.meta = {};
      }
      data.meta = {
        ...data?.meta,
        sender: data["entry"][0]["messaging"][0]["message"]["sender"],
      };
      if (data["entry"][0]["messaging"][0]["message"]?.["repliedOf"]) {
        repliedOf = messageAdapter(
          data["entry"][0]["messaging"][0]["message"]["repliedOf"]
        );
      }
      if (data["entry"][0]["messaging"][0]["message"]?.["sender"]?.["name"]) {
        first_name =
          data["entry"][0]["messaging"][0]["message"]["sender"]["name"];
      }
      reaction = data["entry"][0]["messaging"][0]["message"]?.["reaction"];
      attachments = data["entry"][0]["messaging"][0]["message"]["attachments"];
      attachment = data["entry"][0]["messaging"][0]["message"]["attachment"];
      isDeleted =
        data["entry"][0]["messaging"][0]["message"]["is_deleted"] || false;
    } else if (data["entry"][0]["messaging"][0]["postback"]) {
      textMessage = data["entry"][0]["messaging"][0]["postback"]["title"];
    } else if (
      data["entry"][0]["messaging"][0]["referral"]?.["source"] === "ADS"
    ) {
      attachment = {
        type: "referral",
        payload: {
          url:
            data["entry"][0]["messaging"][0]["referral"]?.[
              "ads_context_data"
            ]?.["photo_url"] ||
            data["entry"][0]["messaging"][0]["referral"]?.[
              "ads_context_data"
            ]?.["video_url"],
          headline:
            data["entry"][0]["messaging"][0]["referral"]?.[
              "ads_context_data"
            ]?.["ad_title"],
          source: `https://www.facebook.com/${
            data["entry"][0]["messaging"][0]["referral"]?.[
              "ads_context_data"
            ]?.["post_id"] ||
            data["entry"][0]["messaging"][0]["referral"]?.["ad_id"]
          }`,
          type: "Post",
        },
      };
    }
  } else {
    textMessage = data?.message?.text || "";
    isDeleted = data?.message?.is_deleted || false;
    reaction = data?.message?.reaction;
    repliedOf =
      data?.message?.repliedOf && messageAdapter(data.message.repliedOf);
    if (!data?.meta && data?.message?.meta) {
      data.meta = data.message.meta
    }
  }

  if (!attachment && data?.message?.attachment) {
    attachment = data?.message?.attachment;
  }

  return {
    id,
    mId: channel_message_id,
    text: textMessage,
    reaction,
    attachment,
    attachments,
    firstName: first_name,
    lastName: last_name,
    deliveryStatus: delivery_status,
    errorCode: error_code,
    errorMessage: error_message,
    senderId: sender_id,
    isFromUser,
    isDeleted,
    metaType: meta_type,
    createdAt: DateTime.fromISO(created_at).toJSDate(),
    confidential,
    meta: data?.meta,
    repliedOf,
  };
};

const campaignAdapter = ({ id, created_at, name, channel }: any): Campaign => ({
  id,
  createdAt: DateTime.fromISO(created_at).toJSDate(),
  name,
  channel,
});

export const linkBot = async (id: number, key: string, password: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    const { error, ...bot } = await response.json();

    if (error) {
      return null;
    }

    return botAdapter(bot);
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getChats = async (
  signal: any,
  id: number,
  key: string,
  password: string,
  term?: string,
  label?: string,
  tab: ChatTab = "all",
  archived?: boolean,
  pageSize?: number,
  userId?: string,
  liveChatFilters?: LiveChatFilters,
  dates?: string[],
  sort?: string,
  channel?: ChatChannel,
  assignee?: string,
  nonBizHour?: number,
  priority?: number,
  currentAgentId?: string,
  userType?: number,
  campaignId?: number,
  boardId?: number,
  boardColumnId?: number,
  boardAssigneeId?: string,
  templateId?: string,
  conversationStatusId?: string,
  campaignStatus?: CampaignInteractionStatus,
  includeTotal = false
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/filter`,
      {
        signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          term,
          label,
          archived,
          tab,
          pageSize,
          userId,
          liveChatFilters,
          dates,
          sort,
          channel,
          assignee,
          nonBizHour,
          priority,
          currentAgentId,
          userType,
          campaignId,
          boardId,
          boardColumnId,
          boardAssigneeId,
          templateId,
          conversationStatusId,
          campaignStatus,
          includeTotal,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error("Unable to load chats");
    }

    // Older callers and deployments still return a plain array.
    const chats = Array.isArray(data) ? data : data.chats;

    return {
      chats: chats.map(chatAdapter) as Chat[],
      total: (Array.isArray(data) ? undefined : data.total) as number | undefined,
    };
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getChat = async (
  id: number,
  key: string,
  password: string,
  chatId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const chat = await response.json();

    return chatAdapter(chat);
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getChatMessages = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  pageSize?: number,
  liveChatFilters?: LiveChatFilters,
  lastMessageId?: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/messages/filter`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          pageSize,
          liveChatFilters,
          lastMessageId,
        }),
      }
    );

    const messages = await response.json();

    return messages?.map(messageAdapter) as Message[];
  } catch (error) {
    logError(error);
    return [];
  }
};

export const markChatMessagesAsRead = async (
  id: number,
  key: string,
  password: string,
  chatId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/messages/actions/read`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const setStarredChat = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  starred: boolean,
  priority?: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/star`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, starred, priority }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const connectToChat = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  userToConnect: User
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/connect`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, userToConnect }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const clearBotForwarding = async (
  id: number,
  key: string,
  password: string,
  chatId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/clearForwarding`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const disconnectFromChat = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  userToDisconnect: User,
  delayedDisconnect: number,
  skipClosingDialog?: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/disconnect`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          userToDisconnect,
          delayedDisconnect,
          skipClosingDialog,
        }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const disconnectAllFromChat = async (
  id: number,
  key: string,
  password: string,
  chatIds?: number[],
  dateRange?: string[],
  liveCardId?: number,
  agentId?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/bulk/disconnect`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          chatIds,
          dateRange,
          liveCardId,
          agentId,
        }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const postPinMessage = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  messageId: number,
  duration: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/messages/actions/pin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, messageId, duration }),
      }
    );

    const { chat } = await response.json();

    return chatAdapter(chat);
  } catch (error) {
    logError(error);
    return false;
  }
};

export const postUnpinMessage = async (
  id: number,
  key: string,
  password: string,
  chatId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/messages/actions/unpin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const postConfidentialMessage = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  messageId: number,
  remove?: boolean,
  liveChatFilters?: LiveChatFilters
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/messages/actions/confidential`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          messageId,
          remove,
          liveChatFilters,
        }),
      }
    );

    const { success, message } = await response.json();

    if (!success && message) {
      throw new Error(message);
    }

    return !!success;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const sendNewMessageToChat = async (
  id: number,
  key: string,
  password: string,
  senderId: string,
  liveCardId: number,
  templateMeta: Record<string, any>,
  userId: string,
  saveAsContact?: boolean,
  firstName?: string,
  lastName?: string,
  type?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          senderId,
          liveCardId,
          templateMeta,
          userId,
          saveAsContact,
          firstName,
          lastName,
          type,
        }),
      }
    );

    const { success, status, response: r } = await response.json();
    if (!success && status === 403) {
      throw new Error(r);
    }
    return chatAdapter(r);
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const sendMessageToChat = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  from: string,
  payload: string | Blob,
  replyId?: number
) => {
  try {
    let body: string | FormData;

    if (typeof payload === "string") {
      body = JSON.stringify({ key, password, text: payload, from, replyId });
    } else {
      body = new FormData();
      body.append("key", key);
      body.append("password", password);
      body.append("from", from);
      if (replyId) {
        body.append("replyId", replyId.toString());
      }
      if (payload.type === "emailTemplate") {
        body.append("file", (payload as any).id);
      } else if (
        [
          "contentTemplate",
          "waFlow",
          "waResponse",
          "reply",
          "contextualReply",
        ].includes(payload.type)
      ) {
        body.append("file", JSON.stringify((payload as any).meta));
      } else {
        body.append("file", payload, (payload as any).name);
      }
      body.append("type", payload.type);
    }

    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/messages`,
      {
        method: "POST",
        headers:
          typeof payload === "string"
            ? {
                "Content-Type": "application/json",
              }
            : {},
        body,
      }
    );

    const { success, status, response: r } = await response.json();
    if (!success && status === 403) {
      throw new Error(r);
    }

    return r.map((m: any) => messageAdapter(m));
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const saveChatData = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/update`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, data }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const saveChatGuestUsers = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  guestUsers: string[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/guestUsers`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, guestUsers }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const setChatLabels = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  label: string,
  type: "add" | "remove",
  deadline?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/labels`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          label,
          type,
          deadline,
        }),
      }
    );

    const { chat } = await response.json();

    return chatAdapter(chat);
  } catch (error) {
    logError(error);
  }
};

export const setChatArchiveStatus = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  archived: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/archive`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, archived }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const saveBotAgentsSchedule = async (
  id: number,
  key: string,
  password: string,
  message?: string,
  dialog?: number,
  agentSchedule?: Schedule,
  livecardSchedule?: Record<string, any>,
  scheduleVersion?: number,
  schedule?: Record<string, any>,
  timezone?: string,
  scheduledChatClosingDialog?: number,
  scheduledChatClosingJourneyId?: number,
  scheduledChatClosingDialogType?: string,
  scheduledChatClosingDialogAt?: number,
  maxChatAssigned?: number,
  chatAssignmentType?: ChatAssignmentType,
  autoAssignChatUserTypes?: number[],
  queuePositionEnabled?: boolean,
  queuePositionDialog?: number,
  enableAutoAssignRequestOnNextDay?: boolean,
  chatTransferredTimeout?: number,
  autoAssignDealUserTypes?: number[],
  autoAssignRoundRobinChat?: boolean,
  roundRobinChatTimeout?: number,
  perCardChatAssignmentConfig?: PerCardChatAssignmentConfig[],
  disableACMForChatTransfer?: boolean,
  disableCCDForChatTransfer?: boolean,
  qfJourneyId?: number,
  qfJourneyConfig?: Record<string, any>,
  qfSenderGroupConfig?: Record<string, any>,
  conversationStatusConfig?: Record<string, any>
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          message,
          dialog,
          agentSchedule,
          livecardSchedule,
          scheduleVersion,
          schedule,
          timezone,
          scheduledChatClosingDialog,
          scheduledChatClosingJourneyId,
          scheduledChatClosingDialogType,
          scheduledChatClosingDialogAt,
          maxChatAssigned,
          chatAssignmentType,
          autoAssignChatUserTypes,
          queuePositionEnabled,
          queuePositionDialog,
          enableAutoAssignRequestOnNextDay,
          chatTransferredTimeout,
          autoAssignDealUserTypes,
          autoAssignRoundRobinChat,
          roundRobinChatTimeout,
          perCardChatAssignmentConfig,
          disableACMForChatTransfer,
          disableCCDForChatTransfer,
          qfJourneyId,
          qfJourneyConfig,
          qfSenderGroupConfig,
          conversationStatusConfig,
        }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const getAssignedChatsByAgent = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/analytics/agents`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return null;
    }

    return r.map((m: any) => ({
      current_agent: m.current_agent,
      ongoingChats: m.ongoingChats,
      ongoingCalls: m.ongoingCalls,
    })) as UserAssignedChat[];
  } catch (error) {
    logError(error);
    return null;
  }
};

export const assignChatToAgent = async (
  id: number,
  key: string,
  password: string,
  userId: string,
  agentId: string,
  chatId: number,
  autoAssign: boolean,
  userToConnect?: User
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/assignAgent`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          userId,
          agentId,
          autoAssign,
          userToConnect,
        }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const assignChatToCard = async (
  id: number,
  key: string,
  password: string,
  userId: string,
  cardId: string,
  chatId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/assignCard`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, cardId, userId }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const respondChatAssignment = async (
  id: number,
  key: string,
  password: string,
  agentId: string,
  chatId: number,
  status: RequestStatus,
  userToConnect: User
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/assignResponse`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, agentId, status, userToConnect }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const sendVideoCallRequest = async (
  id: number,
  agentId: string,
  chatId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/video/request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ agent: agentId, fromAgent: true }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return null;
    }

    return messageAdapter(r[0]);
  } catch (error) {
    logError(error);
    return null;
  }
};

export const respondVideoRequest = async (
  id: number,
  chatId: number,
  messageId: number,
  status: RequestStatus
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/video/response/${messageId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return null;
    }

    return messageAdapter(r);
  } catch (error) {
    logError(error);
    return null;
  }
};

export const updateVideoCallAbility = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  agentId: string,
  disable: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/video/ability`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, agentId, disable }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const callBotmateSkill = async (
  id: number,
  key: string,
  password: string,
  skill: string,
  text: string | undefined,
  messages: any[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/botmate-skills`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ skill, key, password, text, messages }),
      }
    );

    const { success, message: r } = await response.json();

    if (!success) {
      return null;
    }

    return r;
  } catch (error) {
    logError(error);
    return null;
  }
};

export async function getMetric<T = MetricData>(
  botId: number,
  key: string,
  password: string,
  name: string,
  channel?: string,
  from?: string,
  to?: string,
  agents?: string[],
  card?: number,
  customParams?: Record<string, any>,
  options?: { signal?: AbortSignal; throwOnError?: boolean }
): Promise<T[]> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${botId}/analytics/metric/${name}`,
      {
        method: "POST",
        signal: options?.signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          channel,
          from,
          to,
          agents,
          card,
          ...customParams,
        }),
      }
    );

    const { success, data } = await response.json();

    if (options?.throwOnError && (!response.ok || !success)) {
      throw new Error("Unable to load metric");
    }

    if (!success) {
      return [];
    }

    return data as T[];
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    logError(error);
    throw error;
    // return [];
  }
}

export const sendEvents = async (
  id: number,
  botKey: string,
  botPassword: string,
  agentId: string,
  event: string,
  chatId?: number,
  extra?: Record<string, string>
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/events`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          botId: id,
          key: botKey,
          password: botPassword,
          agentId,
          event,
          chatId,
          extra,
        }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

// --- WhatsApp business username (our own @handle on the WABA number) ---

export const getBusinessUsername = async (
  botId: number,
  botKey: string,
  botPassword: string
) => {
  try {
    const params = new URLSearchParams({ key: botKey, password: botPassword });
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${botId}/whatsapp/business-username?${params}`,
      { method: "GET" }
    );
    return await response.json();
  } catch (error) {
    logError(error);
    return { success: false, message: "Request failed" };
  }
};

export const setBusinessUsername = async (
  botId: number,
  botKey: string,
  botPassword: string,
  username: string,
  transferAction?: "none" | "force_transfer"
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${botId}/whatsapp/business-username`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key: botKey,
          password: botPassword,
          username,
          ...(transferAction ? { transfer_action: transferAction } : {}),
        }),
      }
    );
    return await response.json();
  } catch (error) {
    logError(error);
    return { success: false, message: "Request failed" };
  }
};

export const deleteBusinessUsername = async (
  botId: number,
  botKey: string,
  botPassword: string
) => {
  try {
    const params = new URLSearchParams({ key: botKey, password: botPassword });
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${botId}/whatsapp/business-username?${params}`,
      { method: "DELETE" }
    );
    return await response.json();
  } catch (error) {
    logError(error);
    return { success: false, message: "Request failed" };
  }
};

export const getLiveStream = async (id: number) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/stream`,
      { method: "GET" }
    );

    const { success, response: data } = await response.json();

    if (!success) {
      return null;
    }

    return data as LiveStream;
  } catch (error) {
    logError(error);
    return null;
  }
};

export const createLiveStream = async (
  id: number,
  key: string,
  password: string,
  liveCardId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, liveCardId }),
      }
    );

    const { success, response: data } = await response.json();

    if (!success) {
      return null;
    }

    return data as LiveStream;
  } catch (error) {
    logError(error);
    return null;
  }
};

export const disableLiveStream = async (
  id: number,
  key: string,
  password: string,
  streamId: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/stream/${streamId}/stop`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const getLiveStreamConnection = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/stream/connection`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success, response: data } = await response.json();

    if (!success) {
      return null;
    }

    return data as LiveStream;
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getLiveStreamStatus = async (id: number, stream: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/stream/${stream}/status`,
      { method: "GET" }
    );

    const { success, response: data } = await response.json();

    if (!success) {
      return null;
    }

    return data as LiveStream;
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getLiveChatCards = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/card/live-chat`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success, response: data } = await response.json();

    if (!success) {
      return null;
    }

    return data as LiveChatCard[];
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getAllChatsCount = async (
  id: number,
  key: string,
  password: string,
  userId?: string,
  liveChatFilters?: LiveChatFilters,
  assigneeId?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/analytics/all`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          userId,
          liveChatFilters,
          assigneeId,
        }),
      }
    );

    const data = await response.json();

    if (!data) {
      return false;
    }

    return data;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const getCampaigns = async (
  id: number,
  key: string,
  password: string,
  page?: number,
  search?: string,
  from?: string,
  to?: string,
  livecardId?: number,
  userId?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/campaigns/filter`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          page,
          search,
          from,
          to,
          livecardId,
          userId,
        }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return [];
    }

    return r;
  } catch (error) {
    logError(error);
    return null;
  }
};

export class CampaignApiError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = "CampaignApiError";
  }
}

const readCampaignResponse = async (response: Response) => {
  let body: any;
  try {
    body = await response.json();
  } catch {
    throw new CampaignApiError("The campaign service returned an unreadable response.", response.status);
  }
  if (!response.ok || body?.success !== true) {
    throw new CampaignApiError(
      body?.message || "The campaign request could not be completed.",
      response.status,
      body?.code
    );
  }
  return body.response;
};

export const postCampaignFilePreview = async (
  id: number,
  key: string,
  password: string,
  channel: ChatChannel,
  file: File,
  data: string,
  offset = 0,
  issueOffset = 0
) => {
  const body = new FormData();
  body.append("key", key);
  body.append("password", password);
  body.append("channel", channel);
  body.append("file", file);
  body.append("data", data);
  body.append("offset", String(offset));
  body.append("limit", "25");
  body.append("issueOffset", String(issueOffset));
  body.append("issueLimit", "100");
  const response = await fetch(
    `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/campaigns/file-preview`,
    { method: "POST", body }
  );
  return readCampaignResponse(response);
};

export const postCampaign = async (
  id: number,
  key: string,
  password: string,
  name: string,
  channel: ChatChannel,
  data: string,
  campaignDataType: CampaignDataType,
  livecardId: number,
  userId: string,
  file?: File,
  scheduleFor?: string
) => {
  try {
    const body = new FormData();
    body.append("key", key);
    body.append("password", password);
    body.append("name", name);
    body.append("data", data);
    body.append("dataType", campaignDataType);
    body.append("channel", channel);
    body.append("livecard_id", String(livecardId));
    body.append("user_id", userId);
    if (file) {
      body.append("file", file);
    }
    body.append("scheduleFor", scheduleFor || "");
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/campaigns`,
      {
        method: "POST",
        body,
      }
    );

    const campaign = await readCampaignResponse(response);
    if (!campaign?.id) {
      throw new CampaignApiError("The campaign service did not confirm campaign creation.", response.status);
    }
    return campaignAdapter(campaign);
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postEndCampaignStatus = async (
  id: number,
  key: string,
  password: string,
  campaingId: number
) => {
  try {
    const body = new FormData();
    body.append("key", key);
    body.append("password", password);
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/campaigns/${campaingId}/end`,
      {
        method: "POST",
        body,
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      throw new Error(r);
    }
  } catch (error) {
    logError(error);
  }
};

export const getCampaignResults = async (
  id: number,
  key: string,
  password: string,
  campaignId: number,
  pageSize = 50,
  options: { beforeId?: number; status?: CampaignResultStatus; search?: string; signal?: AbortSignal } = {}
): Promise<CampaignResultsData> => {
  const query = new URLSearchParams({ key, password, pageSize: String(pageSize), compact: "1" });
  if (options.beforeId) query.set("beforeId", String(options.beforeId));
  if (options.status && options.status !== "all") query.set("status", options.status);
  if (options.search) query.set("search", options.search);
  const response = await fetch(`${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/campaigns/${campaignId}?${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    signal: options.signal,
  });
  const result = await readCampaignResponse(response);
  if (!result?.campaign || !Array.isArray(result.messages)) {
    throw new CampaignApiError("The campaign results could not be read.", response.status);
  }
  return {
    campaign: {
      ...result.campaign,
      createdAt: DateTime.fromISO(result.campaign.created_at || "").toJSDate(),
    },
    summary: result.summary,
    pagination: result.pagination,
    messages: result.messages.map((raw: any): CampaignResultMessage => {
      let message: Message;
      try {
        message = messageAdapter(raw);
      } catch {
        // A malformed historical message must not hide every other recipient.
        let text = "";
        try {
          const body = typeof raw.text === "string" ? JSON.parse(raw.text) : raw.text;
          text = typeof body?.message?.text === "string" ? body.message.text : "";
        } catch { text = typeof raw.text === "string" ? raw.text : ""; }
        message = {
          id: raw.id, text, senderId: raw.sender_id, firstName: raw.first_name, lastName: raw.last_name,
          deliveryStatus: raw.delivery_status, errorCode: raw.error_code, errorMessage: raw.error_message,
          isFromUser: false, isDeleted: false, createdAt: new Date(NaN),
        };
      }
      if (!Number.isFinite(message.createdAt.getTime())) {
        message.createdAt = DateTime.fromSQL(raw.created_at || "", { zone: "utc" }).toJSDate();
      }
      if (typeof message.text !== "string") message.text = "";
      // A null chat_id means the contact was deleted; never construct a link for it.
      const chatId = Number(raw.chat_id === undefined ? raw.subscribers_id : raw.chat_id);
      return { ...message, chatId: Number.isSafeInteger(chatId) && chatId > 0 ? chatId : undefined };
    }),
  };
};

export const postCampaignTemplateNotification = async (
  id: number,
  key: string,
  password: string,
  userName: string,
  templateId: string,
  type: string
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/campaigns/templates/notify-admin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, userName, templateId, type }),
      }
    );
  } catch (error) {
    logError(error);
    return null;
  }
};

export const postTemplateToTwilio = async (
  username: string,
  password: string,
  body: any
) => {
  const credentials = `${username}:${password}`;
  const base64Credentials = btoa(credentials);
  try {
    const response = await fetch(`https://content.twilio.com/v1/Content`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${base64Credentials}`,
      },
      body: JSON.stringify({ ...body }),
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postTemplateStatusTwilio = async (
  username: string,
  password: string,
  sid: string
) => {
  const credentials = `${username}:${password}`;
  const base64Credentials = btoa(credentials);
  try {
    const response = await fetch(
      `https://content.twilio.com/v1/Content/${sid}/ApprovalRequests`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${base64Credentials}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postTemplateToTwilioForApproval = async (
  username: string,
  password: string,
  sid: string,
  body: any
) => {
  const credentials = `${username}:${password}`;
  const base64Credentials = btoa(credentials);
  try {
    const response = await fetch(
      `https://content.twilio.com/v1/Content/${sid}/ApprovalRequests/whatsapp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${base64Credentials}`,
        },
        body: JSON.stringify({ ...body }),
      }
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return await response.json();
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const deleteTemplateFromTwilio = async (
  username: string,
  password: string,
  sid: string
) => {
  const credentials = `${username}:${password}`;
  const base64Credentials = btoa(credentials);
  try {
    const response = await fetch(
      `https://content.twilio.com/v1/Content/${sid}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${base64Credentials}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getContacts = async (
  id: number,
  key: string,
  password: string,
  term?: string,
  ids?: number[],
  page = 1,
  size = 10,
  orderBy?: string,
  order?: "asc" | "desc",
  label?: string,
  filters?: any,
  all?: boolean,
  liveChatFilters?: LiveChatFilters,
  assignee?: string,
  dateRange?: string[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/contacts/filter?page=${page}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          term,
          ids,
          size,
          orderBy,
          order,
          label,
          filters,
          liveChatFilters,
          assignee,
          all,
          dateRange,
        }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return [];
    }

    return ids || size === 0 || all
      ? (r?.map(chatAdapter) as Chat[])
      : {
          page: r?.current_page as number,
          total: r?.total as number,
          last_page: r?.last_page as number,
          data: r?.data?.map(chatAdapter) as Chat[],
        };
  } catch (error) {
    logError(error);
    return [];
  }
};

export const setContact = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  value: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/markContact`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, value: value ? 1 : 0 }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const findChatBySenderId = async (
  id: number,
  key: string,
  password: string,
  senderId: string,
  channel?: ChatChannel
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/search`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          sender_id: senderId,
          channel,
        }),
      }
    );

    const { chat } = await response.json();

    return chat && chatAdapter(chat);
  } catch (error) {
    logError(error);
    return false;
  }
};

export const findContact = async (
  id: number,
  key: string,
  password: string,
  chatId: string,
  channel: ChatChannel
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/contacts/find`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          id: chatId,
          channel,
        }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const addContact = async (
  id: number,
  key: string,
  password: string,
  firstName: string,
  lastName: string,
  chatId?: string,
  channel?: ChatChannel,
  otherChannel?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/contacts`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          id: chatId,
          firstName,
          lastName,
          channel,
          otherChannel,
        }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return null;
    }

    return chatAdapter(r);
  } catch (error) {
    logError(error);
    return null;
  }
};

export const postCreateChatRoom = async (
  id: number,
  key: string,
  password: string,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/room`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          ...data,
        }),
      }
    );

    const { success, response: r, error } = await response.json();

    if (!success) {
      throw Error(error);
    }

    return chatAdapter(r);
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postImportContact = async (
  id: number,
  key: string,
  password: string,
  file: File,
  fields: any,
  labelsMap: any
) => {
  try {
    const body = new FormData();
    body.append("key", key);
    body.append("password", password);
    body.append("file", file);
    body.append("fields", JSON.stringify(fields));
    if (labelsMap) {
      body.append("labelsMap", JSON.stringify(labelsMap));
    }

    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/contacts/import`,
      {
        method: "POST",
        body,
      }
    );

    await response.json();
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const mergeContacts = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  contacts: number[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/contacts/${chatId}/merge`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, contacts }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const getRatingScore = async (
  id: number,
  key: string,
  password: string,
  ratingType: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/rating/score`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          ratingType,
        }),
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return 0;
    }

    return r as number;
  } catch (error) {
    logError(error);
    return 0;
  }
};

export const setBlockUserStatus = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  value: boolean
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/block`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, value: value ? 1 : 0 }),
      }
    );

    const { success } = await response.json();

    return !!success;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const getProfile = async (
  id: number,
  key: string,
  password: string,
  userId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${userId}/syncProfile`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const { success, data } = await response.json();

    return success ? chatAdapter(data) : null;
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getBotSettings = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/settings?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { success, settings } = await response.json();

    if (success) {
      return settings;
    }
    return false;
  } catch (error) {
    logError(error);
    return false;
  }
};

export const postBotSettings = async (
  id: number,
  key: string,
  password: string,
  type: string,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/settings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, type, data }),
      }
    );

    const { success, message } = await response.json();

    if (!success) {
      throw new Error(message);
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const generateEmbedding = async (id: string, data: EmbeddingData) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/generate-embedding`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data }),
      }
    );

    const { success, message } = await response.json();

    if (!success) {
      throw new Error(message);
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const removeEmbedding = async (id: string, data: EmbeddingData) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/remove-embedding`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data }),
      }
    );

    const { success, message } = await response.json();

    if (!success) {
      throw new Error(message);
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const transferEmbedding = async (
  sourceWorkspaceId: string,
  targetWorkspaceId: string,
  documents: Array<{
    sourceCollectionId: string;
    sourceCollectionName: string;
    targetCollectionId: string;
    targetCollectionName: string;
    text: string;
    meta?: any;
  }>
) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/transfer-embedding`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceWorkspaceId,
          targetWorkspaceId,
          documents,
        }),
      }
    );

    const { success, message } = await response.json();

    if (!success) {
      throw new Error(message);
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const callEdnaBot = async (
  id: string,
  message: string,
  embeddingTypes: string[],
  meta: any,
  lang?: string
) => {
  try {
    const token = await auth.getBearerToken();

    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/call-edna-bot`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message, embeddingTypes, meta, lang: "es" }),
      }
    );

    const { success, response: r } = await response.json();

    if (success) {
      return r;
    }
    throw new Error("Error");
  } catch (error) {
    logError(error);
    return "Something went wrong";
  }
};

export const getAITeams = async (id: string) => {
  try {
    const token = await auth.getBearerToken();

    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/ai-teams`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const { success, response: r } = await response.json();

    if (!success) {
      return {};
    }

    return r;
  } catch (error) {
    logError(error);
    return null;
  }
};

export const callAITeam = async (
  id: string,
  chatId: string,
  aITeamId: string,
  team: string,
  message: string,
  messages?: any[],
  userId?: string
) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/ai-teams/message`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chatId,
          aITeamId,
          message,
          team,
          messages,
          userId,
        }),
      }
    );

    const { success, response: r } = await response.json();

    if (success) {
      return r;
    }
    throw new Error("Error");
  } catch (error) {
    logError(error);
    return "Something went wrong";
  }
};

export const storeErrorLogs = async (
  id: number,
  key: string,
  password: string,
  error: string,
  stack: string,
  email?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/error-log`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          error,
          stack,
          email,
        }),
      }
    );

    const { success } = await response.json();

    if (!success) {
      throw new Error("Error while reporting error");
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const notifyUserForNewTask = async (
  id: number,
  key: string,
  password: string,
  email: string,
  ticketId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/board/item/notify-user`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, email, ticketId }),
      }
    );
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getHelpCenterConfig = async (workspaceId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/getHelpCenterConfig`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getHelpCenterCollections = async (workspaceId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/getHelpCenterCollections`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getHelpCenterCollection = async (
  workspaceId: string,
  collectionId: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/getHelpCenterCollection`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId, collectionId },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getHelpCenterArticle = async (
  workspaceId: string,
  articleId: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/getArticle`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId, articleId },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getHelpCenterSearchArticles = async (
  workspaceId: string,
  search: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/getHelpCenterSearchArticles`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId, search },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getWorkflowCategories = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/category?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createWorkflowCategory = async (
  id: number,
  key: string,
  password: string,
  name: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/category`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateWorkflowCategory = async (
  id: number,
  key: string,
  password: string,
  workflowCategoryId: number,
  name: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/category/${workflowCategoryId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const deleteWorkflowCategory = async (
  id: number,
  key: string,
  password: string,
  workflowCategoryId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/category/${workflowCategoryId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getWorkflowNodes = async (
  id: number,
  key: string,
  password: string,
  lang: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/nodes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          lang,
        }),
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getWorkflows = async (
  id: number,
  key: string,
  password: string,
  categoryId?: number,
  filters?: any
) => {
  try {
    const response = await fetch(
      `${
        process.env.REACT_APP_CHATHUB_APP_URL
      }/api/${id}/workflow?categoryId=${categoryId}&key=${key}&password=${password}&archived=${
        filters?.archived ? 1 : 0
      }`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getWorkflowEvents = async (
  id: number,
  key: string,
  password: string,
  workflowId?: number,
  page = 1
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/${workflowId}/event?page=${page}&key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getWorkflowLogs = async (
  id: number,
  key: string,
  password: string,
  workflowId?: number,
  page = 1
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/${workflowId}/log?page=${page}&key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getActiveWorkflowEvents = async (
  id: number,
  key: string,
  password: string,
  chatId?: string,
  status?: string,
  page = 1
) => {
  try {
    let url = `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/active?page=${page}&key=${key}&password=${password}`;

    if (chatId) {
      url += `&chatId=${chatId}`;
    }

    if (status) {
      url += `&status=${status}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const cancelWorkflowEvent = async (
  id: number,
  key: string,
  password: string,
  eventId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/event/${eventId}/cancel?key=${key}&password=${password}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getWorkflow = async (
  id: number,
  key: string,
  password: string,
  workflowId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/${workflowId}?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createWorkflow = async (
  id: number,
  key: string,
  password: string,
  name: string,
  categoryId: number,
  userId: string,
  status: string,
  isActive: boolean,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          categoryId,
          userId,
          status,
          isActive,
          data,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateWorkflow = async (
  id: number,
  key: string,
  password: string,
  workflowId: number,
  name: string,
  categoryId: number,
  userId: string,
  status: string,
  isActive: boolean,
  archived: boolean,
  updatedBy: string,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/${workflowId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          categoryId,
          userId,
          status,
          isActive,
          archived,
          updatedBy,
          data,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateConversationStatus = async (
  id: number,
  key: string,
  password: string,
  chatId: number,
  statusId: string,
  agentName: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/chats/${chatId}/actions/conversationStatus`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          statusId,
          agentName,
        }),
      }
    );

    const { success } = await response.json();
    if (!success) {
      throw new Error("Failed to update conversation status");
    }
  } catch (error) {
    logError(error);
  }
};

export const deleteWorkflow = async (
  id: number,
  key: string,
  password: string,
  workflowId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/${workflowId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const triggerWorkflow = async (
  id: number,
  key: string,
  password: string,
  workflowTriggerKey: string,
  workflowMeta: string
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/workflow/trigger`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          workflowTriggerKey,
          workflowMeta,
        }),
      }
    );
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getJourneyCategories = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/category?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createJourneyCategory = async (
  id: number,
  key: string,
  password: string,
  name: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/category`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateJourneyCategory = async (
  id: number,
  key: string,
  password: string,
  journeyCategoryId: number,
  name: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/category/${journeyCategoryId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const deleteJourneyCategory = async (
  id: number,
  key: string,
  password: string,
  journeyCategoryId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/category/${journeyCategoryId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getJourneyNodes = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/nodes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getJourneys = async (
  id: number,
  key: string,
  password: string,
  categoryId?: number,
  filters?: any,
  page = 1,
  size = 10,
  search?: string,
  sortField?: string,
  sortOrder?: "asc" | "desc"
) => {
  try {
    const response = await fetch(
      `${
        process.env.REACT_APP_CHATHUB_APP_URL
      }/api/${id}/journey?categoryId=${categoryId}&key=${key}&password=${password}&archived=${
        filters?.archived ? 1 : 0
      }&page=${page}&size=${size}${search ? `&search=${search}` : ""}${
        sortField ? `&sort_field=${sortField}` : ""
      }${sortOrder ? `&sort_order=${sortOrder}` : ""}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getJourneyEvents = async (
  id: number,
  key: string,
  password: string,
  journeyId?: number,
  page = 1
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/${journeyId}/event?page=${page}&key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getJourneyLogs = async (
  id: number,
  key: string,
  password: string,
  journeyId?: number,
  page = 1
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/${journeyId}/log?page=${page}&key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getJourney = async (
  id: number,
  key: string,
  password: string,
  journeyId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/${journeyId}?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createJourney = async (
  id: number,
  key: string,
  password: string,
  name: string,
  categoryId: number,
  userId: string,
  status: string,
  isActive: boolean,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          categoryId,
          userId,
          status,
          isActive,
          data,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateJourney = async (
  id: number,
  key: string,
  password: string,
  journeyId: number,
  name: string,
  categoryId: number,
  userId: string,
  status: string,
  isActive: boolean,
  archived: boolean,
  updatedBy: string,
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/${journeyId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          categoryId,
          userId,
          status,
          isActive,
          archived,
          updatedBy,
          data,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const duplicateJourney = async (
  id: number,
  key: string,
  password: string,
  journeyId: number,
  userId: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/${journeyId}/duplicate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          userId,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const deleteJourney = async (
  id: number,
  key: string,
  password: string,
  journeyId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/${journeyId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const bulkDeleteJourneys = async (
  id: number,
  key: string,
  password: string,
  journeyIds: number[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/bulk-delete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          journeyIds,
        }),
      }
    );

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.message || "Failed to delete journeys");
    }

    return result;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getAllJourneys = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/all`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getTriggerableJourneys = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/triggerable`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password }),
      }
    );

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || "Failed to load triggerable Journeys");
    }

    return result.response;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getJourneyFlowMap = async (
  id: number,
  key: string,
  password: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/journey/flow-map?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getCards = async (
  id: number,
  key: string,
  password: string,
  page = 1,
  size = 10,
  search?: string,
  includeV1LiveChat?: boolean,
  onlyActive?: boolean,
  sortField?: string,
  sortOrder?: "asc" | "desc"
) => {
  try {
    const response = await fetch(
      `${
        process.env.REACT_APP_CHATHUB_APP_URL
      }/api/${id}/card?key=${key}&password=${password}&page=${page}&size=${size}&search=${
        search ? search : ""
      }&include_v1_live_chat=${includeV1LiveChat ? 1 : 0}&only_active=${
        onlyActive ? 1 : 0
      }${sortField ? `&sort_field=${sortField}` : ""}${
        sortOrder ? `&sort_order=${sortOrder}` : ""
      }`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createCard = async (
  id: number,
  key: string,
  password: string,
  name: string,
  status: boolean,
  type: string,
  data?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/card`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          type,
          status,
          data,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateCard = async (
  id: number,
  key: string,
  password: string,
  cardId: number,
  name: string,
  status: boolean,
  data?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/card/${cardId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          status,
          data,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const deleteCard = async (
  id: number,
  key: string,
  password: string,
  cardId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/card/${cardId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const bulkDeleteCards = async (
  id: number,
  key: string,
  password: string,
  cardIds: number[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/card/bulk-delete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          cardIds,
        }),
      }
    );

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.message || "Failed to delete cards");
    }

    return result;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getForms = async (
  id: number,
  key: string,
  password: string,
  page = 1,
  size = 10,
  search?: string,
  sortField?: string,
  sortOrder?: "asc" | "desc"
) => {
  try {
    const response = await fetch(
      `${
        process.env.REACT_APP_CHATHUB_APP_URL
      }/api/${id}/form?key=${key}&password=${password}&page=${page}&size=${size}&search=${
        search ? search : ""
      }${sortField ? `&sort_field=${sortField}` : ""}${
        sortOrder ? `&sort_order=${sortOrder}` : ""
      }`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getFormById = async (
  id: number,
  key: string,
  password: string,
  formId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/form/${formId}?key=${key}&password=${password}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createForm = async (
  id: number,
  key: string,
  password: string,
  name: string,
  branch?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/form`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          branch,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateForm = async (
  id: number,
  key: string,
  password: string,
  formId: number,
  name: string,
  branch?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/form/${formId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          branch,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const deleteForm = async (
  id: number,
  key: string,
  password: string,
  formId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/form/${formId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const duplicateForm = async (
  id: number,
  key: string,
  password: string,
  formId: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/form/${formId}/duplicate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const bulkDeleteForms = async (
  id: number,
  key: string,
  password: string,
  formIds: number[]
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/form/bulk-delete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          formIds,
        }),
      }
    );

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.message || "Failed to delete forms");
    }

    return result;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const sendFCM = async (
  id: number,
  key: string,
  password: string,
  userIds: string[],
  title: string,
  body: string
) => {
  try {
    await fetch(`${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/send-fcm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        key,
        password,
        userIds,
        title,
        body,
      }),
    });
  } catch (error) {
    logError(error);
    return null;
  }
};

export const getKpis = async (
  id: number,
  key: string,
  password: string,
  filters?: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/analytics/kpi/all`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          filters,
        }),
      }
    );

    const { response: r } = await response.json();

    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createKpi = async (
  id: number,
  key: string,
  password: string,
  name: string,
  createdByUserId: string,
  target: number,
  symbol?: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/analytics/kpi`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          name,
          createdByUserId,
          target,
          symbol,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const updateKpi = async (
  id: number,
  key: string,
  password: string,
  kpiId: number,
  target: number
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/analytics/kpi/${kpiId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
          target,
        }),
      }
    );

    const { response: r } = await response.json();
    return r;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const destroyKpi = async (
  id: number,
  key: string,
  password: string,
  kpiId: number
) => {
  try {
    await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/analytics/kpi/${kpiId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key,
          password,
        }),
      }
    );
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const addBotToFunction = async (
  id: string,
  botId: string,
  botKey: string,
  botPassword: string
) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/add-bot`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          botId,
          botKey,
          botPassword,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const loadCalendarSetting = async (
  workspaceId: string,
  eventId: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/getCalendarSetting`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId, eventId },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const createBooking = async (workspaceId: string, booking: Booking) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/createBooking`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId, booking },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const searchContactInFirebase = async (
  workspaceId: string,
  id: string
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/searchChat`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: { workspaceId, id },
        }),
      }
    );

    const { result } = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postFBRedirectCallback = async (id: string, data: any) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/redirect-callback/facebook`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      }
    );

    const result = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postGoogleRedirectCallback = async (id: string, data: any) => {
  try {
    const token = await auth.getBearerToken();
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/redirect-callback/google`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      }
    );

    const result = await response.json();

    if (!result.success) {
      throw new Error("Something went wrong");
    }

    return result.data;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postWorkspaceEvent = async (id: string, type: string) => {
  try {
    const token = await auth.getBearerToken();

    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/workspace/${id}/events/notify`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type }),
      }
    );

    const { success, response: r } = await response.json();

    if (success) {
      return r;
    }
    throw new Error("Error");
  } catch (error) {
    logError(error);
    return "Something went wrong";
  }
};

export const postTestIMAP_SMTP = async (
  id: number,
  key: string,
  password: string,
  type: "imap" | "smtp",
  data: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/test/imap-smtp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, type, data }),
      }
    );

    const { message } = await response.json();
    return message;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const postTemplateToMetaWhatsapp = async (
  id: number,
  key: string,
  password: string,
  path: string,
  method: string,
  body: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/meta-template`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, password, path, method, body }),
      }
    );
    const res = await response.json();

    if (response.status === 400) {
      throw new Error(
        res?.error?.error_user_msg ?? res?.error?.error_user_title
      );
    }

    return res;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const handleMetaWhatsappFlow = async (
  id: number,
  key: string,
  password: string,
  flowData: any
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${id}/meta-whatsapp-flow?key=${key}&password=${password}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(flowData),
      }
    );

    const result = await response.json();

    if (!result.success) {
      throw new Error(
        result?.error?.error?.error_user_title ||
          "Failed to create WhatsApp flow"
      );
    }

    return result;
  } catch (error) {
    logError(error);
    throw error;
  }
};
